import { useCallback, useEffect, useRef, useState } from "react"
import { HeroSection } from "./HeroSection"
import { Separator } from "./Separator"
import { SiteFooter } from "./SiteFooter"
import { SiteHeader } from "./SiteHeader"
import { Builder } from "./builder/Builder"
import { TermsPage } from "./TermsPage"
import { StringsProvider } from "@/hooks/useStrings"
import { strings } from "@/strings"

const INTERSECTION_THRESHOLDS = Array.from({ length: 21 }, (_, index) => index / 20)
const HEADER_HEIGHT = 56

const clamp = (value: number) => Math.min(1, Math.max(0, value))

export function App() {
  const [heroNode, setHeroNode] = useState<HTMLElement | null>(null)
  const [heroProgress, setHeroProgress] = useState(0)
  const [widgetVisible, setWidgetVisible] = useState(false)
  const [isTermsPage, setIsTermsPage] = useState(false)
  const [stickyTop, setStickyTop] = useState<number | null>(null)
  const stickyRef = useRef<HTMLDivElement | null>(null)
  const registerHeroRef = useCallback((node: HTMLElement | null) => setHeroNode(node), [])

  // console.log('widget visible', widgetVisible, heroProgress)

  useEffect(() => {
    if (!heroNode || typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setHeroProgress(1)
      setWidgetVisible(false)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const nextProgress = entry ? clamp(1 - entry.intersectionRatio) : 1
        setHeroProgress(previous => (Math.abs(previous - nextProgress) < 0.02 ? previous : nextProgress))
        const nextVisible = nextProgress > 0.8 //entry.intersectionRatio === 0 && entry.boundingClientRect.top <= -16
        // console.log(nextProgress, entry?.intersectionRatio, nextVisible)
        setWidgetVisible(previous => (previous === nextVisible ? previous : nextVisible))
      },
      { threshold: INTERSECTION_THRESHOLDS, rootMargin: "-16px 0px 0px 0px" }
    )
    observer.observe(heroNode)
    return () => {
      observer.unobserve(heroNode)
      observer.disconnect()
    }
  }, [heroNode])

  useEffect(() => {
    if (typeof window === "undefined" || !stickyRef.current) return
    const node = stickyRef.current
    let raf = 0
    const measure = () => {
      const rect = node.getBoundingClientRect()
      const nextTop = HEADER_HEIGHT - rect.height
      setStickyTop(prev => (prev === null || Math.abs(prev - nextTop) > 0.5 ? nextTop : prev))
    }
    const schedule = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        measure()
      })
    }

    measure()
    window.addEventListener("resize", schedule)
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null
    resizeObserver?.observe(node)

    return () => {
      window.removeEventListener("resize", schedule)
      resizeObserver?.disconnect()
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return
    const syncTerms = () => {
      const { hash } = window.location
      setIsTermsPage(hash.startsWith("#/terms"))
    }
    syncTerms()
    window.addEventListener("hashchange", syncTerms)
    return () => window.removeEventListener("hashchange", syncTerms)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return
    window.scrollTo({ top: 0, left: 0, behavior: "auto" })
    if (!isTermsPage && stickyRef.current) {
      const rect = stickyRef.current.getBoundingClientRect()
      const nextTop = HEADER_HEIGHT - rect.height
      setStickyTop(prev => (prev === null || Math.abs(prev - nextTop) > 0.5 ? nextTop : prev))
    }
  }, [isTermsPage])

  const handleTermsClose = useCallback(() => {
    if (typeof window !== "undefined") window.location.hash = "#/"
  }, [])

  return (
    <div className="min-h-screen min-w-screen bg-background text-foreground flex flex-col">
      {isTermsPage ? (
        <SiteHeader
          gradientInterpolant={1}
          showMobileClose
          onMobileClose={handleTermsClose}
          className="terms-header-fade terms-header-fixed"
        />
      ) : (
        <div
          ref={stickyRef}
          className="bg-gradient-to-b from-background to-muted sticky z-1"
          style={stickyTop === null ? undefined : { top: `${stickyTop}px` }}
        >
          <SiteHeader gradientInterpolant={heroProgress} />
          <HeroSection ref={registerHeroRef} />
          <Separator />
        </div>
      )}
      <main className="flex-1">
        {isTermsPage ? (
          <TermsPage />
        ) : (
          <StringsProvider value={strings}>
            <Builder widgetVisible={widgetVisible} />
          </StringsProvider>
        )}
        {/* <FakeContent/> */}
      </main>
      <SiteFooter />
    </div>
  )
}

export default App
